import html2canvas from 'html2canvas';

const SCHOOL_NAME = 'The Fast School & Academy';
const ROWS_PER_PAGE = 13;

function formatMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  return v.toLocaleString('en-PK', { maximumFractionDigits: 0 });
}

function cleanField(value) {
  return String(value ?? '')
    .replace(/^[\s"'“”‘’]+|[\s"'“”‘’]+$/g, '')
    .trim();
}

function displayName(line) {
  const roll = cleanField(line.roll_number);
  const name = cleanField(line.student_name);
  if (roll && name) return `(${roll}) ${name}`;
  return roll ? `(${roll})` : name;
}

function sumField(rows, field) {
  return rows.reduce((sum, row) => sum + (Number(row[field]) || 0), 0);
}

function chunkRows(rows, size) {
  const pages = [];
  for (let i = 0; i < rows.length; i += size) {
    pages.push(rows.slice(i, i + size));
  }
  return pages.length ? pages : [[]];
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Prepare printable fee pages (max 13 rows each). No window.open.
 */
export function prepareFeePrint(lines) {
  const rows = Array.isArray(lines) ? lines.filter(Boolean) : [];
  if (!rows.length) {
    throw new Error('Select or filter rows to print');
  }

  const dateLabel = new Date().toLocaleDateString('en-GB');
  const pages = chunkRows(rows, ROWS_PER_PAGE);
  const totals = {
    fees: sumField(rows, 'fees'),
    arrear: sumField(rows, 'arrear'),
    payable: sumField(rows, 'payable'),
    amount: sumField(rows, 'amount'),
    balance: sumField(rows, 'balance')
  };

  return {
    rows,
    pages,
    dateLabel,
    totals,
    rowCount: rows.length,
    pageCount: pages.length,
    schoolName: SCHOOL_NAME,
    displayName,
    formatMoney
  };
}

// Compact Excel-style receipt — small type, almost no cell padding.
const CELL = 'border:1px solid #000;padding:7px 8px;line-height:1.1;vertical-align:middle;background:#fff;color:#000;font-family:Arial,Helvetica,sans-serif;font-size:11px;white-space:nowrap;';
const TH = `${CELL}font-weight:700;text-align:center;`;
const TD_NAME = `${CELL}text-align:left;font-weight:400;`;
const TD_CENTER = `${CELL}text-align:center;font-weight:400;`;
const TD_NUM = `${CELL}text-align:right;font-weight:400;`;
const TD_LEFT = `${CELL}text-align:left;font-weight:400;`;

function buildPageTableHtml(pageRows, totals, dateLabel) {
  const body = pageRows
    .map(
      (line) => `
      <tr>
        <td style="${TD_NAME}">${escapeHtml(displayName(line))}</td>
        <td style="${TD_CENTER}">${escapeHtml(cleanField(line.class_name))}</td>
        <td style="${TD_LEFT}">${escapeHtml(cleanField(line.payment_plan))}</td>
        <td style="${TD_NUM}">${escapeHtml(formatMoney(line.fees))}</td>
        <td style="${TD_NUM}">${escapeHtml(formatMoney(line.arrear))}</td>
        <td style="${TD_NUM}">${escapeHtml(formatMoney(line.payable))}</td>
        <td style="${TD_NUM}">${escapeHtml(Number(line.amount) ? formatMoney(line.amount) : '')}</td>
        <td style="${TD_NUM}">${escapeHtml(formatMoney(line.balance))}</td>
      </tr>`
    )
    .join('');

  return `
    <section class="fee-print-page" style="margin:0;padding:0;width:max-content;background:#fff;">
      <table class="fee-print-table" style="width:max-content;border-collapse:collapse;border-spacing:0;margin:0;background:#fff;color:#000;">
        <thead>
          <tr>
            <th colspan="5" style="${TH}text-align:left;">${escapeHtml(SCHOOL_NAME)}</th>
            <th colspan="3" style="${TH}text-align:right;font-weight:400;"><strong>Date:</strong> ${escapeHtml(dateLabel)}</th>
          </tr>
          <tr>
            <th style="${TH}">Last Name</th>
            <th style="${TH}">Class</th>
            <th style="${TH}">Payment Plan</th>
            <th style="${TH}">Fees</th>
            <th style="${TH}">Arrears</th>
            <th style="${TH}">Payable</th>
            <th style="${TH}">Amount</th>
            <th style="${TH}">Balance</th>
          </tr>
        </thead>
        <tbody>${body}</tbody>
        <tfoot>
          <tr>
            <td colspan="3" style="${TD_LEFT}"><strong>Grand Total:</strong></td>
            <td style="${TD_NUM}"><strong>${escapeHtml(formatMoney(totals.fees))}</strong></td>
            <td style="${TD_NUM}"><strong>${escapeHtml(formatMoney(totals.arrear))}</strong></td>
            <td style="${TD_NUM}"><strong>${escapeHtml(formatMoney(totals.payable))}</strong></td>
            <td style="${TD_NUM}"><strong>${escapeHtml(formatMoney(totals.amount))}</strong></td>
            <td style="${TD_NUM}"><strong>${escapeHtml(formatMoney(totals.balance))}</strong></td>
          </tr>
        </tfoot>
      </table>
    </section>`;
}

function buildPrintDocumentHtml(preview) {
  const pagesHtml = preview.pages
    .map((pageRows) => buildPageTableHtml(pageRows, preview.totals, preview.dateLabel))
    .join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Fee Print</title>
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 0;
      font-family: Arial, Helvetica, sans-serif;
      color: #000;
      background: #fff;
    }
    .fee-print-page {
      padding: 0;
      margin: 0;
      page-break-after: always;
      width: max-content;
      max-width: 100%;
    }
    .fee-print-page:last-child { page-break-after: auto; }
    .fee-print-table {
      width: max-content;
      border-collapse: collapse;
      border-spacing: 0;
      font-size: 11px;
      line-height: 1.1;
      color: #000;
      background: #fff;
    }
    .fee-print-table th,
    .fee-print-table td {
      border: 1px solid #000;
      padding: 7px 8px;
      vertical-align: middle;
      background: #fff;
      color: #000;
      white-space: nowrap;
    }
    @page {
      margin: 3mm;
    }
  </style>
</head>
<body>${pagesHtml}</body>
</html>`;
}

/**
 * Print via hidden iframe (no pop-up window).
 */
export function printFeeReceipt(previewOrLines) {
  const preview = Array.isArray(previewOrLines)
    ? prepareFeePrint(previewOrLines)
    : previewOrLines;

  if (!preview?.rows?.length) {
    throw new Error('Select or filter rows to print');
  }

  const html = buildPrintDocumentHtml(preview);
  let iframe = document.getElementById('fees-print-iframe');
  if (!iframe) {
    iframe = document.createElement('iframe');
    iframe.id = 'fees-print-iframe';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
      'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);
  }

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  const doPrint = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (err) {
      throw new Error(err.message || 'Print failed');
    }
  };

  // Give the iframe a moment to finish layout before opening print dialog
  setTimeout(doPrint, 150);
}

const FEE_PRINT_CAPTURE_CSS = `
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    overflow: visible !important;
  }
  .fee-print-capture {
    background: #fff !important;
    color: #000;
    font-family: Arial, Helvetica, sans-serif;
    padding: 8px;
    margin: 0;
    width: max-content !important;
    max-width: none !important;
    display: inline-block;
    box-sizing: content-box;
  }
  .fee-print-page {
    margin: 0;
    padding: 0;
    width: max-content !important;
    max-width: none !important;
  }
  .fee-print-table {
    width: max-content !important;
    max-width: none !important;
    border-collapse: collapse;
    table-layout: auto;
  }
`;

function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Empty screenshot'))),
      'image/png'
    );
  });
}

/** Trim empty / white margins so WhatsApp does not show a tiny strip in a huge frame. */
function cropWhiteMargins(sourceCanvas, padding = 4) {
  const ctx = sourceCanvas.getContext('2d', { willReadFrequently: true });
  const { width, height } = sourceCanvas;
  if (!width || !height) return sourceCanvas;

  const { data } = ctx.getImageData(0, 0, width, height);
  const isContent = (i) => {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    // non-white / non-transparent pixel
    return a > 8 && (r < 250 || g < 250 || b < 250);
  };

  let top = 0;
  let bottom = height - 1;
  let left = 0;
  let right = width - 1;
  let found = false;

  outerTop: for (; top < height; top++) {
    for (let x = 0; x < width; x++) {
      if (isContent((top * width + x) * 4)) {
        found = true;
        break outerTop;
      }
    }
  }
  if (!found) return sourceCanvas;

  outerBottom: for (; bottom >= top; bottom--) {
    for (let x = 0; x < width; x++) {
      if (isContent((bottom * width + x) * 4)) break outerBottom;
    }
  }
  outerLeft: for (; left < width; left++) {
    for (let y = top; y <= bottom; y++) {
      if (isContent((y * width + left) * 4)) break outerLeft;
    }
  }
  outerRight: for (; right >= left; right--) {
    for (let y = top; y <= bottom; y++) {
      if (isContent((y * width + right) * 4)) break outerRight;
    }
  }

  // Keep a generous pad so right-edge borders / last digits are never shaved off.
  const pad = Math.max(padding, 8);
  const sx = Math.max(0, left - pad);
  const sy = Math.max(0, top - pad);
  const sw = Math.min(width - sx, right - left + 1 + pad * 2);
  const sh = Math.min(height - sy, bottom - top + 1 + pad * 2);

  if (sw >= width - 2 && sh >= height - 2) return sourceCanvas;
  // If crop would remove more than ~2% of width, skip — clipping bug, not margin.
  if (sw < width * 0.98) return sourceCanvas;

  const cropped = document.createElement('canvas');
  cropped.width = sw;
  cropped.height = sh;
  cropped.getContext('2d').drawImage(sourceCanvas, sx, sy, sw, sh, 0, 0, sw, sh);
  return cropped;
}

/**
 * Convert PNG blob → clipboard as a real image (not a filename / blob URL string).
 * Critical for LAN HTTP (no secure Clipboard API) and WhatsApp paste.
 */

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read screenshot'));
    reader.readAsDataURL(blob);
  });
}

async function tryClipboardWritePng(blob) {
  if (
    typeof navigator === 'undefined'
    || !navigator.clipboard
    || typeof navigator.clipboard.write !== 'function'
    || !window.ClipboardItem
  ) {
    return false;
  }

  const pngBlob =
    blob.type === 'image/png'
      ? blob
      : new Blob([blob], { type: 'image/png' });

  // Prefer Promise form — more reliable across Chrome versions
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'image/png': Promise.resolve(pngBlob)
      })
    ]);
    return true;
  } catch (_) {
    /* try sync blob form */
  }

  try {
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': pngBlob })
    ]);
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Copy-event path: put only image/png on the clipboard (no text/filename).
 */
function copyPngViaCopyEvent(blob) {
  // Generic name — some targets still show a name; MIME image/png is what matters.
  const file = new File([blob], 'image.png', { type: 'image/png' });
  return new Promise((resolve, reject) => {
    const onCopy = (e) => {
      try {
        e.preventDefault();
        e.stopPropagation();
        if (e.clipboardData) {
          e.clipboardData.clearData();
          // Do NOT set text/plain — that is what pastes as the image name.
          e.clipboardData.items.add(file);
        }
        resolve();
      } catch (err) {
        reject(err);
      } finally {
        document.removeEventListener('copy', onCopy, true);
      }
    };
    document.addEventListener('copy', onCopy, true);
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (err) {
      document.removeEventListener('copy', onCopy, true);
      reject(err);
      return;
    }
    if (!ok) {
      document.removeEventListener('copy', onCopy, true);
      reject(new Error('Could not copy image'));
    }
  });
}

/**
 * Select a data-URL <img> inside contentEditable and copy.
 * Data URLs copy as real image pixels; blob: URLs often paste as a link/name.
 */
function copyImageViaDataUrlSelection(blob) {
  return new Promise(async (resolve, reject) => {
    let dataUrl;
    try {
      dataUrl = await blobToDataUrl(blob);
    } catch (err) {
      reject(err);
      return;
    }
    if (!dataUrl.startsWith('data:image/')) {
      reject(new Error('Invalid screenshot data'));
      return;
    }

    const img = new Image();
    img.alt = '';
    img.setAttribute('alt', '');
    img.draggable = false;

    img.onload = () => {
      const holder = document.createElement('div');
      holder.contentEditable = 'true';
      holder.setAttribute('contenteditable', 'true');
      Object.assign(holder.style, {
        position: 'fixed',
        left: '8px',
        top: '8px',
        width: `${Math.min(img.naturalWidth || 400, 640)}px`,
        zIndex: '2147483647',
        opacity: '0.02',
        pointerEvents: 'auto',
        background: '#fff',
        outline: 'none',
        overflow: 'hidden'
      });
      // Avoid any text nodes that could become clipboard text
      holder.replaceChildren(img);
      document.body.appendChild(holder);

      try {
        holder.focus();
        const range = document.createRange();
        // selectNodeContents copies rich HTML with embedded image better than selectNode
        range.selectNodeContents(holder);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        const ok = document.execCommand('copy');
        sel.removeAllRanges();
        if (ok) resolve();
        else reject(new Error('Could not copy image'));
      } catch (err) {
        reject(err);
      } finally {
        try {
          document.body.removeChild(holder);
        } catch (_) {
          /* ignore */
        }
      }
    };

    img.onerror = () => reject(new Error('Failed to load screenshot image'));
    img.src = dataUrl;
  });
}

/** Copy using the already-visible preview <img> (after forcing data URL if needed). */
async function copyImageFromVisibleElement(img, blob) {
  if (!img) throw new Error('No image to copy');

  // If preview still uses blob: URL, swap to data URL for this copy attempt
  let restoreSrc = null;
  if (blob && (!img.src || img.src.startsWith('blob:'))) {
    restoreSrc = img.src;
    img.src = await blobToDataUrl(blob);
    await new Promise((resolve, reject) => {
      if (img.complete && img.naturalWidth) {
        resolve();
        return;
      }
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load screenshot image'));
    });
  }

  const holder = document.createElement('div');
  holder.contentEditable = 'true';
  Object.assign(holder.style, {
    position: 'fixed',
    left: '8px',
    top: '8px',
    zIndex: '2147483647',
    opacity: '0.02',
    background: '#fff',
    outline: 'none'
  });
  const clone = img.cloneNode(true);
  clone.alt = '';
  clone.removeAttribute('title');
  clone.removeAttribute('alt');
  holder.replaceChildren(clone);
  document.body.appendChild(holder);

  try {
    holder.focus();
    const range = document.createRange();
    range.selectNodeContents(holder);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    const ok = document.execCommand('copy');
    sel.removeAllRanges();
    if (!ok) throw new Error('Could not copy image');
    return { method: 'selection' };
  } finally {
    try {
      document.body.removeChild(holder);
    } catch (_) {
      /* ignore */
    }
    if (restoreSrc != null) {
      try {
        img.src = restoreSrc;
      } catch (_) {
        /* ignore */
      }
    }
  }
}

async function copyPngBlob(blob) {
  if (await tryClipboardWritePng(blob)) {
    return { method: 'clipboard' };
  }

  try {
    await copyImageViaDataUrlSelection(blob);
    return { method: 'selection' };
  } catch (_) {
    /* fall through */
  }

  await copyPngViaCopyEvent(blob);
  return { method: 'copy-event' };
}

/**
 * Capture fee receipt PNG (no clipboard). Use with a Copy button for LAN HTTP.
 */
export async function captureFeeReceiptPng(previewOrLines) {
  const preview = Array.isArray(previewOrLines)
    ? prepareFeePrint(previewOrLines)
    : previewOrLines;

  if (!preview?.rows?.length) {
    throw new Error('Select or filter rows to screenshot');
  }

  const blob = await renderFeeReceiptPng(preview);
  return {
    blob,
    pageCount: preview.pageCount,
    rowCount: preview.rowCount
  };
}

/**
 * Copy a PNG blob to the clipboard as image pixels (not a filename).
 * Call from a user click handler. Optional visibleImg helps on plain HTTP.
 */
export async function copyFeePngBlob(blob, visibleImg = null) {
  if (!blob) throw new Error('No screenshot to copy');

  // Ensure MIME is image/png for clipboard consumers (WhatsApp, etc.)
  const pngBlob =
    blob.type === 'image/png'
      ? blob
      : new Blob([await blob.arrayBuffer()], { type: 'image/png' });

  // 1) Modern clipboard API (HTTPS / localhost) — true image/png
  if (await tryClipboardWritePng(pngBlob)) {
    return { method: 'clipboard' };
  }

  // 2) Data-URL selection — copies image pixels, not a filename (best on LAN HTTP)
  try {
    await copyImageViaDataUrlSelection(pngBlob);
    return { method: 'selection' };
  } catch (_) {
    /* fall through */
  }

  // 3) Visible preview image
  if (visibleImg) {
    try {
      return await copyImageFromVisibleElement(visibleImg, pngBlob);
    } catch (_) {
      /* fall through */
    }
  }

  // 4) Last resort: copy-event with PNG file
  await copyPngViaCopyEvent(pngBlob);
  return { method: 'copy-event' };
}

function buildCaptureHost(pagesHtml) {
  // Dedicated iframe so the table can be wider than the app viewport.
  // html2canvas clips wide tables when the host uses transform/off-screen tricks.
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('title', 'fee-receipt-capture');
  Object.assign(iframe.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: '3200px',
    height: '2400px',
    opacity: '0.01',
    pointerEvents: 'none',
    border: '0',
    zIndex: '2147483646',
    background: '#fff'
  });
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <style>${FEE_PRINT_CAPTURE_CSS}</style>
</head>
<body>
  <div class="fee-print-capture">${pagesHtml}</div>
</body>
</html>`);
  doc.close();
  return iframe;
}

async function renderFeeReceiptPng(preview) {
  // One continuous table for WhatsApp — print still paginates at 13 rows.
  const pagesHtml = buildPageTableHtml(preview.rows, preview.totals, preview.dateLabel);

  const iframe = buildCaptureHost(pagesHtml);

  try {
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    // Extra tick so iframe layout finishes (fonts / table width).
    await new Promise((r) => setTimeout(r, 50));

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    const target = doc.querySelector('.fee-print-capture');
    const table = target?.querySelector('table');
    if (!target || !table) throw new Error('Screenshot layout missing');

    const tableRect = table.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const w = Math.ceil(Math.max(tableRect.width, targetRect.width, table.scrollWidth, target.scrollWidth) + 16);
    const h = Math.ceil(Math.max(tableRect.height, targetRect.height, table.scrollHeight, target.scrollHeight) + 16);

    // Resize iframe so html2canvas is not limited by a smaller frame.
    iframe.style.width = `${Math.max(w + 40, 800)}px`;
    iframe.style.height = `${Math.max(h + 40, 400)}px`;
    target.style.width = `${w}px`;
    target.style.height = `${h}px`;

    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const canvas = await html2canvas(target, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
      width: w,
      height: h,
      windowWidth: w,
      windowHeight: h,
      x: 0,
      y: 0
    });

    if (!canvas.width || !canvas.height) {
      throw new Error('Screenshot capture was empty');
    }

    // Prefer full canvas — only trim tiny outer whitespace, never clip columns.
    const cropped = cropWhiteMargins(canvas, 8);
    return canvasToPngBlob(cropped);
  } finally {
    try {
      document.body.removeChild(iframe);
    } catch (_) {
      /* ignore */
    }
  }
}

/**
 * Capture the same print layout as a PNG and copy it to the clipboard (for WhatsApp).
 */
export async function screenshotFeeReceipt(previewOrLines) {
  const preview = Array.isArray(previewOrLines)
    ? prepareFeePrint(previewOrLines)
    : previewOrLines;

  if (!preview?.rows?.length) {
    throw new Error('Select or filter rows to screenshot');
  }

  const blob = await renderFeeReceiptPng(preview);
  const result = await copyPngBlob(blob);
  return {
    ok: true,
    pageCount: preview.pageCount,
    rowCount: preview.rowCount,
    copyMethod: result.method
  };
}

/**
 * Clipboard-first path: start navigator.clipboard.write in the click turn,
 * resolve the PNG inside ClipboardItem. Fixes "success but nothing pasted".
 */
export async function screenshotFeeReceiptFromClick(previewOrLines) {
  const preview = Array.isArray(previewOrLines)
    ? prepareFeePrint(previewOrLines)
    : previewOrLines;

  if (!preview?.rows?.length) {
    throw new Error('Select or filter rows to screenshot');
  }

  const canUseClipboardApi =
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.write === 'function' &&
    window.ClipboardItem &&
    window.isSecureContext;

  if (canUseClipboardApi) {
    let captured = null;
    const blobPromise = renderFeeReceiptPng(preview).then((blob) => {
      captured = blob;
      return blob;
    });

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': blobPromise
        })
      ]);
      return {
        ok: true,
        pageCount: preview.pageCount,
        rowCount: preview.rowCount,
        copyMethod: 'clipboard'
      };
    } catch (_) {
      const blob = captured || (await blobPromise);
      const result = await copyPngBlob(blob);
      return {
        ok: true,
        pageCount: preview.pageCount,
        rowCount: preview.rowCount,
        copyMethod: result.method
      };
    }
  }

  const blob = await renderFeeReceiptPng(preview);
  const result = await copyPngBlob(blob);
  return {
    ok: true,
    pageCount: preview.pageCount,
    rowCount: preview.rowCount,
    copyMethod: result.method
  };
}

export { SCHOOL_NAME, ROWS_PER_PAGE };
